// src/components/admin/VendorManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import { FaBuilding, FaSearch, FaCheck, FaTimes, FaBan, FaTrash, FaEye, FaEdit, FaSync, FaPlus } from 'react-icons/fa';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/api$/, '');

const TIER_COLORS = {
  Bronze: '#cd7f32', Silver: '#9e9e9e', Gold: '#e6ac00', Platinum: '#6B8E23', Diamond: '#0d6efd'
};

const STATUS_BADGES = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
  suspended: 'secondary'
};

const VendorManagement = () => {
  const [vendors, setVendors] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0, suspended: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editLogoPreview, setEditLogoPreview] = useState(null);
  const [editLogo, setEditLogo] = useState(null);
  const [editDoc, setEditDoc] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ vendor_tier: 'Bronze', vendor_price: 150, status: 'pending' });
  const [addLogoPreview, setAddLogoPreview] = useState(null);
  const [addLogo, setAddLogo] = useState(null);
  const [addDoc, setAddDoc] = useState(null);
  const [actionLoading, setActionLoading] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const LIMIT = 20;

  const getToken = () =>
    localStorage.getItem('adminToken') ||
    localStorage.getItem('authToken') ||
    localStorage.getItem('userToken') || '';

  const authHeader = () => ({ 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' });

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/vendors/stats`, { headers: authHeader() });
      const data = await res.json();
      if (data.success) setStats(data.data);
    } catch (err) { console.error('Stats error:', err); }
  }, []);

  const fetchVendors = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit: LIMIT });
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (countryFilter) params.append('country', countryFilter);

      const res = await fetch(`${API_BASE}/api/vendors?${params}`, { headers: authHeader() });
      const data = await res.json();
      if (data.success) {
        setVendors(data.data);
        setTotal(data.total);
      }
    } catch (err) { console.error('Fetch vendors error:', err); }
    finally { setLoading(false); }
  }, [search, statusFilter, countryFilter, page]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  const handleStatusChange = async (id, status, companyName) => {
    if (!window.confirm(`${status.charAt(0).toUpperCase() + status.slice(1)} vendor "${companyName}"?`)) return;
    setActionLoading(id + status);
    try {
      const res = await fetch(`${API_BASE}/api/vendors/${id}/status`, {
        method: 'PUT',
        headers: authHeader(),
        body: JSON.stringify({ status })
      });
      const data = await res.json();
      if (data.success) {
        fetchVendors();
        fetchStats();
        if (showDetail && selectedVendor?.id === id) {
          setSelectedVendor(prev => ({ ...prev, status }));
        }
        alert(`Vendor ${status} successfully.`);
      } else {
        alert(data.message || 'Failed to update status');
      }
    } catch (err) {
      alert('Error updating vendor status');
    } finally { setActionLoading(''); }
  };

  const handleDelete = async (id, companyName) => {
    if (!window.confirm(`Permanently delete vendor "${companyName}"? This cannot be undone.`)) return;
    setActionLoading(id + 'delete');
    try {
      const res = await fetch(`${API_BASE}/api/vendors/${id}`, { method: 'DELETE', headers: authHeader() });
      const data = await res.json();
      if (data.success) {
        fetchVendors();
        fetchStats();
        if (showDetail && selectedVendor?.id === id) setShowDetail(false);
        alert('Vendor deleted successfully.');
      } else {
        alert(data.message || 'Failed to delete vendor');
      }
    } catch (err) {
      alert('Error deleting vendor');
    } finally { setActionLoading(''); }
  };

  const openDetail = async (vendor) => {
    try {
      const res = await fetch(`${API_BASE}/api/vendors/${vendor.id}`, { headers: authHeader() });
      const data = await res.json();
      if (data.success) { setSelectedVendor(data.data); setShowDetail(true); }
    } catch (err) { console.error(err); }
  };

  const openEdit = async (vendor) => {
    try {
      const res = await fetch(`${API_BASE}/api/vendors/${vendor.id}`, { headers: authHeader() });
      const data = await res.json();
      if (data.success) {
        setEditForm(data.data);
        setEditLogoPreview(data.data.logo ? getLogoUrl(data.data.logo) : null);
        setEditLogo(null);
        setEditDoc(null);
        setShowEdit(true);
      }
    } catch (err) { console.error(err); }
  };

  const handleEditSubmit = async () => {
    setActionLoading('edit');
    try {
      const formData = new FormData();
      const fields = ['company_name', 'contact_person', 'contact_person_title', 'email', 'phone',
        'country', 'company_address', 'street_address', 'city', 'state_province', 'postal_code',
        'category', 'website', 'description', 'company_size', 'partnership_goals', 'vendor_tier', 'vendor_price', 'status'];
      fields.forEach(f => formData.append(f, editForm[f] || ''));
      if (editLogo) formData.append('logo', editLogo);
      if (editDoc) formData.append('document', editDoc);

      const res = await fetch(`${API_BASE}/api/vendors/${editForm.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: formData
      });
      const data = await res.json();
      if (data.success) {
        setShowEdit(false);
        fetchVendors();
        fetchStats();
        alert('Vendor updated successfully.');
      } else {
        alert(data.message || 'Failed to update vendor');
      }
    } catch (err) {
      alert('Error updating vendor');
    } finally { setActionLoading(''); }
  };

  const getLogoUrl = (logo) => {
    if (!logo) return null;
    if (logo.startsWith('http')) return logo;
    return `${API_BASE}${logo}`;
  };

  const openAdd = () => {
    setAddForm({ vendor_tier: 'Bronze', vendor_price: 150, status: 'pending' });
    setAddLogoPreview(null);
    setAddLogo(null);
    setAddDoc(null);
    setShowAdd(true);
  };

  const handleAddSubmit = async () => {
    if (!addForm.company_name || !addForm.contact_person || !addForm.email || !addForm.phone || !addForm.country) {
      alert('Company name, contact person, email, phone, and country are required.');
      return;
    }
    setActionLoading('add');
    try {
      const formData = new FormData();
      const fields = ['company_name', 'contact_person', 'contact_person_title', 'email', 'phone',
        'country', 'company_address', 'street_address', 'city', 'state_province', 'postal_code',
        'category', 'website', 'description', 'company_size', 'partnership_goals', 'vendor_tier', 'vendor_price', 'status'];
      fields.forEach(f => { if (addForm[f] !== undefined && addForm[f] !== '') formData.append(f, addForm[f]); });
      const nameParts = (addForm.contact_person || '').trim().split(' ');
      formData.append('first_name', nameParts[0] || '');
      formData.append('last_name', nameParts.slice(1).join(' ') || nameParts[0] || '');
      formData.append('title', addForm.contact_person_title || '');
      if (addLogo) formData.append('logo', addLogo);
      if (addDoc) formData.append('document', addDoc);

      const res = await fetch(`${API_BASE}/api/vendors/register`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: formData
      });
      const data = await res.json();
      if (data.success) {
        setShowAdd(false);
        fetchVendors();
        fetchStats();
        alert('Vendor added successfully. A set-password email has been sent to the vendor.');
      } else {
        alert(data.message || 'Failed to add vendor');
      }
    } catch (err) {
      alert('Error adding vendor');
    } finally { setActionLoading(''); }
  };

  const countries = [...new Set(vendors.map(v => v.country).filter(Boolean))].sort();
  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div className="d-flex align-items-center">
          <FaBuilding size={28} style={{ color: '#6B8E23' }} className="me-3" />
          <div>
            <h2 className="h4 fw-bold mb-0">Vendor Management</h2>
            <small className="text-muted">Manage industry partner applications and directory</small>
          </div>
        </div>
        <div className="d-flex gap-2">
          <button onClick={openAdd} className="btn btn-sm text-white fw-semibold d-flex align-items-center" style={{ backgroundColor: '#6B8E23', border: 'none' }}>
            <FaPlus className="me-2" /> Add Vendor
          </button>
          <button onClick={() => { fetchVendors(); fetchStats(); }} className="btn btn-sm btn-outline-secondary d-flex align-items-center">
            <FaSync className="me-2" /> Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="row g-3 mb-4">
        {[
          { label: 'Total', value: stats.total, color: '#6B8E23', filter: '' },
          { label: 'Pending', value: stats.pending, color: '#ffc107', filter: 'pending' },
          { label: 'Approved', value: stats.approved, color: '#28a745', filter: 'approved' },
          { label: 'Rejected', value: stats.rejected, color: '#dc3545', filter: 'rejected' },
          { label: 'Suspended', value: stats.suspended, color: '#6c757d', filter: 'suspended' }
        ].map(s => (
          <div key={s.label} className="col-6 col-md-4 col-lg-2">
            <div
              className="card border-0 shadow-sm text-center p-3 h-100"
              style={{ borderTop: `4px solid ${s.color}`, cursor: 'pointer', transition: 'all 0.2s' }}
              onClick={() => { setStatusFilter(s.filter); setPage(1); }}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <div className="fw-bold fs-3 mb-1" style={{ color: s.color }}>{s.value}</div>
              <div className="text-muted small">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body p-3">
          <div className="row g-3 align-items-center">
            <div className="col-12 col-md-5">
              <div className="input-group">
                <span className="input-group-text bg-white"><FaSearch className="text-muted" /></span>
                <input
                  type="text" className="form-control" placeholder="Search by company, email, contact..."
                  value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                />
              </div>
            </div>
            <div className="col-6 col-md-3">
              <select className="form-select" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
            <div className="col-6 col-md-3">
              <select className="form-select" value={countryFilter} onChange={e => { setCountryFilter(e.target.value); setPage(1); }}>
                <option value="">All Countries</option>
                {countries.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="col-12 col-md-1">
              <button className="btn btn-outline-secondary w-100" onClick={() => { setSearch(''); setStatusFilter(''); setCountryFilter(''); setPage(1); }}>
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Vendors Table */}
      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border" style={{ color: '#6B8E23' }} role="status" />
              <p className="text-muted mt-3">Loading vendors...</p>
            </div>
          ) : vendors.length === 0 ? (
            <div className="text-center py-5">
              <FaBuilding size={48} className="text-muted mb-3" />
              <p className="text-muted">No vendors found.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead style={{ backgroundColor: '#f8f9fa' }}>
                  <tr>
                    <th className="px-3 py-3">Company</th>
                    <th className="px-3 py-3">Contact</th>
                    <th className="px-3 py-3">Country</th>
                    <th className="px-3 py-3">Tier</th>
                    <th className="px-3 py-3">Status</th>
                    <th className="px-3 py-3">Registered</th>
                    <th className="px-3 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vendors.map(vendor => (
                    <tr key={vendor.id}>
                      <td className="px-3 py-3">
                        <div className="d-flex align-items-center">
                          {getLogoUrl(vendor.logo) ? (
                            <img
                              src={getLogoUrl(vendor.logo)}
                              alt={vendor.company_name}
                              style={{ width: '36px', height: '36px', objectFit: 'contain', borderRadius: '4px', border: '1px solid #dee2e6', marginRight: '10px', backgroundColor: '#f8f9fa', padding: '2px' }}
                              onError={e => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <div className="me-2 rounded d-flex align-items-center justify-content-center" style={{ width: '36px', height: '36px', backgroundColor: '#f0f4e8' }}>
                              <FaBuilding style={{ color: '#6B8E23', fontSize: '16px' }} />
                            </div>
                          )}
                          <div>
                            <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>{vendor.company_name}</div>
                            <small className="text-muted">{vendor.email}</small>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div style={{ fontSize: '0.875rem' }}>{vendor.contact_person}</div>
                        <small className="text-muted">{vendor.phone}</small>
                      </td>
                      <td className="px-3 py-3"><small>{vendor.country}</small></td>
                      <td className="px-3 py-3">
                        <span className="badge" style={{ backgroundColor: TIER_COLORS[vendor.vendor_tier] || '#6B8E23', fontSize: '0.75rem' }}>
                          {vendor.vendor_tier || 'Bronze'}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <span className={`badge bg-${STATUS_BADGES[vendor.status] || 'secondary'}`}>
                          {vendor.status?.charAt(0).toUpperCase() + vendor.status?.slice(1)}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <small className="text-muted">{new Date(vendor.created_at).toLocaleDateString()}</small>
                      </td>
                      <td className="px-3 py-3">
                        <div className="d-flex gap-1 justify-content-center flex-wrap">
                          <button
                            className="btn btn-sm btn-outline-primary" title="View Details"
                            onClick={() => openDetail(vendor)}
                          >
                            <FaEye />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-secondary" title="Edit Vendor"
                            onClick={() => openEdit(vendor)}
                          >
                            <FaEdit />
                          </button>
                          {vendor.status !== 'approved' && (
                            <button
                              className="btn btn-sm btn-outline-success" title="Approve"
                              disabled={actionLoading === vendor.id + 'approved'}
                              onClick={() => handleStatusChange(vendor.id, 'approved', vendor.company_name)}
                            >
                              {actionLoading === vendor.id + 'approved' ? <span className="spinner-border spinner-border-sm" /> : <FaCheck />}
                            </button>
                          )}
                          {vendor.status !== 'rejected' && (
                            <button
                              className="btn btn-sm btn-outline-danger" title="Reject"
                              disabled={actionLoading === vendor.id + 'rejected'}
                              onClick={() => handleStatusChange(vendor.id, 'rejected', vendor.company_name)}
                            >
                              {actionLoading === vendor.id + 'rejected' ? <span className="spinner-border spinner-border-sm" /> : <FaTimes />}
                            </button>
                          )}
                          {vendor.status === 'approved' && (
                            <button
                              className="btn btn-sm btn-outline-secondary" title="Suspend"
                              disabled={actionLoading === vendor.id + 'suspended'}
                              onClick={() => handleStatusChange(vendor.id, 'suspended', vendor.company_name)}
                            >
                              {actionLoading === vendor.id + 'suspended' ? <span className="spinner-border spinner-border-sm" /> : <FaBan />}
                            </button>
                          )}
                          <button
                            className="btn btn-sm btn-outline-danger" title="Delete"
                            disabled={actionLoading === vendor.id + 'delete'}
                            onClick={() => handleDelete(vendor.id, vendor.company_name)}
                          >
                            {actionLoading === vendor.id + 'delete' ? <span className="spinner-border spinner-border-sm" /> : <FaTrash />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="card-footer bg-white d-flex align-items-center justify-content-between px-3 py-2">
            <small className="text-muted">Showing {((page - 1) * LIMIT) + 1}–{Math.min(page * LIMIT, total)} of {total}</small>
            <div className="d-flex gap-2">
              <button className="btn btn-sm btn-outline-secondary" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                return (
                  <button key={p} className={`btn btn-sm ${p === page ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setPage(p)}>{p}</button>
                );
              })}
              <button className="btn btn-sm btn-outline-secondary" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Vendor Detail Modal */}
      {showDetail && selectedVendor && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }} onClick={() => setShowDetail(false)}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#6B8E23', color: 'white' }}>
                <h5 className="modal-title fw-bold d-flex align-items-center">
                  <FaBuilding className="me-2" /> {selectedVendor.company_name}
                </h5>
                <button className="btn-close btn-close-white" onClick={() => setShowDetail(false)} />
              </div>
              <div className="modal-body p-4">
                <div className="row g-4">
                  {/* Logo */}
                  {getLogoUrl(selectedVendor.logo) && (
                    <div className="col-12 text-center">
                      <img
                        src={getLogoUrl(selectedVendor.logo)}
                        alt={selectedVendor.company_name}
                        style={{ maxHeight: '120px', maxWidth: '300px', objectFit: 'contain', border: '1px solid #dee2e6', padding: '8px', borderRadius: '8px' }}
                        onError={e => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  )}

                  {/* Status & Tier */}
                  <div className="col-12">
                    <div className="d-flex gap-2 flex-wrap">
                      <span className={`badge bg-${STATUS_BADGES[selectedVendor.status] || 'secondary'} fs-6`}>
                        {selectedVendor.status?.charAt(0).toUpperCase() + selectedVendor.status?.slice(1)}
                      </span>
                      <span className="badge fs-6" style={{ backgroundColor: TIER_COLORS[selectedVendor.vendor_tier] || '#6B8E23' }}>
                        {selectedVendor.vendor_tier} Tier — ${selectedVendor.vendor_price}/year
                      </span>
                    </div>
                  </div>

                  {/* Company Info */}
                  <div className="col-md-6">
                    <h6 className="fw-bold text-muted text-uppercase mb-3" style={{ fontSize: '0.75rem' }}>Contact Information</h6>
                    <table className="table table-sm table-borderless">
                      <tbody>
                        <tr><td className="text-muted small" style={{ width: '40%' }}>Contact Person</td><td className="fw-medium small">{selectedVendor.contact_person} {selectedVendor.contact_person_title && `(${selectedVendor.contact_person_title})`}</td></tr>
                        <tr><td className="text-muted small">Email</td><td className="fw-medium small">{selectedVendor.email}</td></tr>
                        <tr><td className="text-muted small">Phone</td><td className="fw-medium small">{selectedVendor.phone}</td></tr>
                        <tr><td className="text-muted small">Country</td><td className="fw-medium small">{selectedVendor.country}</td></tr>
                        {selectedVendor.website && <tr><td className="text-muted small">Website</td><td><a href={selectedVendor.website.startsWith('http') ? selectedVendor.website : `https://${selectedVendor.website}`} target="_blank" rel="noopener noreferrer" className="small">{selectedVendor.website}</a></td></tr>}
                        {selectedVendor.company_size && <tr><td className="text-muted small">Company Size</td><td className="small">{selectedVendor.company_size}</td></tr>}
                      </tbody>
                    </table>
                  </div>

                  <div className="col-md-6">
                    <h6 className="fw-bold text-muted text-uppercase mb-3" style={{ fontSize: '0.75rem' }}>Registration Details</h6>
                    <table className="table table-sm table-borderless">
                      <tbody>
                        <tr><td className="text-muted small" style={{ width: '40%' }}>Registered</td><td className="small">{new Date(selectedVendor.created_at).toLocaleDateString()}</td></tr>
                        <tr><td className="text-muted small">Category</td><td className="small">{selectedVendor.category || 'N/A'}</td></tr>
                        {selectedVendor.document && (
                          <tr>
                            <td className="text-muted small">Document</td>
                            <td><a href={`${API_BASE}${selectedVendor.document}`} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">View Document</a></td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Description */}
                  {selectedVendor.description && (
                    <div className="col-12">
                      <h6 className="fw-bold text-muted text-uppercase mb-2" style={{ fontSize: '0.75rem' }}>Company Description</h6>
                      <p className="small text-muted border rounded p-3 bg-light">{selectedVendor.description}</p>
                    </div>
                  )}

                  {/* Categories */}
                  {selectedVendor.categories && (
                    <div className="col-12">
                      <h6 className="fw-bold text-muted text-uppercase mb-2" style={{ fontSize: '0.75rem' }}>Areas of Concentration</h6>
                      <div className="d-flex flex-wrap gap-2">
                        {(() => {
                          try {
                            const cats = typeof selectedVendor.categories === 'string' ? JSON.parse(selectedVendor.categories) : selectedVendor.categories;
                            return cats.map(c => <span key={c} className="badge" style={{ backgroundColor: '#6B8E23' }}>{c}</span>);
                          } catch { return <span className="badge bg-secondary">{selectedVendor.category}</span>; }
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Partnership Goals */}
                  {selectedVendor.partnership_goals && (
                    <div className="col-12">
                      <h6 className="fw-bold text-muted text-uppercase mb-2" style={{ fontSize: '0.75rem' }}>Partnership Goals</h6>
                      <p className="small text-muted border rounded p-3 bg-light">{selectedVendor.partnership_goals}</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer flex-wrap gap-2">
                {selectedVendor.status !== 'approved' && (
                  <button className="btn btn-success" disabled={actionLoading === selectedVendor.id + 'approved'} onClick={() => handleStatusChange(selectedVendor.id, 'approved', selectedVendor.company_name)}>
                    <FaCheck className="me-2" /> Approve
                  </button>
                )}
                {selectedVendor.status !== 'rejected' && (
                  <button className="btn btn-danger" disabled={actionLoading === selectedVendor.id + 'rejected'} onClick={() => handleStatusChange(selectedVendor.id, 'rejected', selectedVendor.company_name)}>
                    <FaTimes className="me-2" /> Reject
                  </button>
                )}
                {selectedVendor.status === 'approved' && (
                  <button className="btn btn-secondary" disabled={actionLoading === selectedVendor.id + 'suspended'} onClick={() => handleStatusChange(selectedVendor.id, 'suspended', selectedVendor.company_name)}>
                    <FaBan className="me-2" /> Suspend
                  </button>
                )}
                {selectedVendor.status !== 'pending' && (
                  <button className="btn btn-outline-secondary" onClick={() => handleStatusChange(selectedVendor.id, 'pending', selectedVendor.company_name)}>
                    Reset to Pending
                  </button>
                )}
                <button className="btn btn-outline-secondary" onClick={() => { setShowDetail(false); openEdit(selectedVendor); }}>
                  <FaEdit className="me-2" /> Edit
                </button>
                <button className="btn btn-outline-danger ms-auto" onClick={() => handleDelete(selectedVendor.id, selectedVendor.company_name)}>
                  <FaTrash className="me-2" /> Delete
                </button>
                <button className="btn btn-outline-secondary" onClick={() => setShowDetail(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Vendor Modal ── */}
      {showEdit && editForm && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1070 }} onClick={() => setShowEdit(false)}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#6B8E23', color: 'white' }}>
                <h5 className="modal-title fw-bold"><FaEdit className="me-2" /> Edit Vendor — {editForm.company_name}</h5>
                <button className="btn-close btn-close-white" onClick={() => setShowEdit(false)} />
              </div>
              <div className="modal-body p-4">
                <div className="row g-3">
                  {/* Company Name */}
                  <div className="col-12">
                    <label className="form-label fw-medium">Company / Organization Name <span className="text-danger">*</span></label>
                    <input className="form-control" value={editForm.company_name || ''} onChange={e => setEditForm(f => ({ ...f, company_name: e.target.value }))} />
                  </div>
                  {/* Contact Person */}
                  <div className="col-md-6">
                    <label className="form-label fw-medium">Contact Person</label>
                    <input className="form-control" value={editForm.contact_person || ''} onChange={e => setEditForm(f => ({ ...f, contact_person: e.target.value }))} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-medium">Contact Person Title</label>
                    <input className="form-control" value={editForm.contact_person_title || ''} onChange={e => setEditForm(f => ({ ...f, contact_person_title: e.target.value }))} />
                  </div>
                  {/* Email / Phone */}
                  <div className="col-md-6">
                    <label className="form-label fw-medium">Email</label>
                    <input type="email" className="form-control" value={editForm.email || ''} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-medium">Phone</label>
                    <input className="form-control" value={editForm.phone || ''} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} />
                  </div>
                  {/* Country / Website */}
                  <div className="col-md-6">
                    <label className="form-label fw-medium">Country</label>
                    <input className="form-control" value={editForm.country || ''} onChange={e => setEditForm(f => ({ ...f, country: e.target.value }))} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-medium">Website</label>
                    <input type="url" className="form-control" value={editForm.website || ''} onChange={e => setEditForm(f => ({ ...f, website: e.target.value }))} />
                  </div>
                  {/* Tier / Status */}
                  <div className="col-md-4">
                    <label className="form-label fw-medium">Vendor Tier</label>
                    <select className="form-select" value={editForm.vendor_tier || 'Bronze'} onChange={e => {
                      const tierPrices = { Bronze: 150, Silver: 200, Gold: 250, Platinum: 300, Diamond: 500 };
                      setEditForm(f => ({ ...f, vendor_tier: e.target.value, vendor_price: tierPrices[e.target.value] }));
                    }}>
                      <option value="Bronze">Bronze — $150/yr</option>
                      <option value="Silver">Silver — $200/yr</option>
                      <option value="Gold">Gold — $250/yr</option>
                      <option value="Platinum">Platinum — $300/yr</option>
                      <option value="Diamond">Diamond — $500/yr</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-medium">Company Size</label>
                    <select className="form-select" value={editForm.company_size || ''} onChange={e => setEditForm(f => ({ ...f, company_size: e.target.value }))}>
                      <option value="">Select size</option>
                      <option value="1-10">1–10 employees</option>
                      <option value="11-50">11–50 employees</option>
                      <option value="51-200">51–200 employees</option>
                      <option value="201-1000">201–1000 employees</option>
                      <option value="1000+">1000+ employees</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-medium">Status</label>
                    <select className="form-select" value={editForm.status || 'pending'} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </div>
                  {/* Address */}
                  <div className="col-12">
                    <label className="form-label fw-medium">Company Address</label>
                    <textarea className="form-control" rows={2} value={editForm.company_address || ''} onChange={e => setEditForm(f => ({ ...f, company_address: e.target.value }))} />
                  </div>
                  {/* Description */}
                  <div className="col-12">
                    <label className="form-label fw-medium">Company Description</label>
                    <textarea className="form-control" rows={3} value={editForm.description || ''} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} />
                  </div>
                  {/* Partnership Goals */}
                  <div className="col-12">
                    <label className="form-label fw-medium">Partnership Goals</label>
                    <textarea className="form-control" rows={2} value={editForm.partnership_goals || ''} onChange={e => setEditForm(f => ({ ...f, partnership_goals: e.target.value }))} />
                  </div>
                  {/* Logo */}
                  <div className="col-md-6">
                    <label className="form-label fw-medium">Company Logo</label>
                    {editLogoPreview && (
                      <div className="mb-2">
                        <img src={editLogoPreview} alt="Logo" style={{ maxHeight: '70px', maxWidth: '180px', objectFit: 'contain', border: '1px solid #dee2e6', borderRadius: '4px', padding: '4px' }} />
                      </div>
                    )}
                    <input type="file" className="form-control" accept="image/*" onChange={e => {
                      const file = e.target.files[0];
                      if (file) {
                        setEditLogo(file);
                        const reader = new FileReader();
                        reader.onload = ev => setEditLogoPreview(ev.target.result);
                        reader.readAsDataURL(file);
                      }
                    }} />
                    <small className="text-muted">Leave blank to keep current logo</small>
                  </div>
                  {/* Document */}
                  <div className="col-md-6">
                    <label className="form-label fw-medium">Business License / Document</label>
                    {editForm.document && <p className="small text-muted mb-1">Current: <a href={`${API_BASE}${editForm.document}`} target="_blank" rel="noopener noreferrer">View</a></p>}
                    <input type="file" className="form-control" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={e => setEditDoc(e.target.files[0])} />
                    <small className="text-muted">Leave blank to keep current document</small>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowEdit(false)}>Cancel</button>
                <button
                  className="btn text-white fw-bold"
                  style={{ backgroundColor: '#6B8E23', border: 'none' }}
                  disabled={actionLoading === 'edit'}
                  onClick={handleEditSubmit}
                >
                  {actionLoading === 'edit' ? <><span className="spinner-border spinner-border-sm me-2" />Saving...</> : <><FaCheck className="me-2" />Save Changes</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ── Add Vendor Modal ── */}
      {showAdd && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1080 }} onClick={() => setShowAdd(false)}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#6B8E23', color: 'white' }}>
                <h5 className="modal-title fw-bold"><FaPlus className="me-2" /> Add New Vendor</h5>
                <button className="btn-close btn-close-white" onClick={() => setShowAdd(false)} />
              </div>
              <div className="modal-body p-4">
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label fw-medium">Company / Organization Name <span className="text-danger">*</span></label>
                    <input className="form-control" placeholder="e.g. MedTech Solutions Ltd." value={addForm.company_name || ''} onChange={e => setAddForm(f => ({ ...f, company_name: e.target.value }))} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-medium">Contact Person <span className="text-danger">*</span></label>
                    <input className="form-control" placeholder="Full name" value={addForm.contact_person || ''} onChange={e => setAddForm(f => ({ ...f, contact_person: e.target.value }))} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-medium">Contact Person Title</label>
                    <input className="form-control" placeholder="e.g. CEO, Manager" value={addForm.contact_person_title || ''} onChange={e => setAddForm(f => ({ ...f, contact_person_title: e.target.value }))} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-medium">Email <span className="text-danger">*</span></label>
                    <input type="email" className="form-control" placeholder="vendor@company.com" value={addForm.email || ''} onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-medium">Phone <span className="text-danger">*</span></label>
                    <input className="form-control" placeholder="+1 234 567 8900" value={addForm.phone || ''} onChange={e => setAddForm(f => ({ ...f, phone: e.target.value }))} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-medium">Country <span className="text-danger">*</span></label>
                    <input className="form-control" placeholder="e.g. Ghana" value={addForm.country || ''} onChange={e => setAddForm(f => ({ ...f, country: e.target.value }))} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-medium">Website</label>
                    <input type="url" className="form-control" placeholder="https://company.com" value={addForm.website || ''} onChange={e => setAddForm(f => ({ ...f, website: e.target.value }))} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-medium">Vendor Tier</label>
                    <select className="form-select" value={addForm.vendor_tier || 'Bronze'} onChange={e => {
                      const tierPrices = { Bronze: 150, Silver: 200, Gold: 250, Platinum: 300, Diamond: 500 };
                      setAddForm(f => ({ ...f, vendor_tier: e.target.value, vendor_price: tierPrices[e.target.value] }));
                    }}>
                      <option value="Bronze">Bronze — $150/yr</option>
                      <option value="Silver">Silver — $200/yr</option>
                      <option value="Gold">Gold — $250/yr</option>
                      <option value="Platinum">Platinum — $300/yr</option>
                      <option value="Diamond">Diamond — $500/yr</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-medium">Company Size</label>
                    <select className="form-select" value={addForm.company_size || ''} onChange={e => setAddForm(f => ({ ...f, company_size: e.target.value }))}>
                      <option value="">Select size</option>
                      <option value="1-10">1–10 employees</option>
                      <option value="11-50">11–50 employees</option>
                      <option value="51-200">51–200 employees</option>
                      <option value="201-1000">201–1000 employees</option>
                      <option value="1000+">1000+ employees</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-medium">Initial Status</label>
                    <select className="form-select" value={addForm.status || 'pending'} onChange={e => setAddForm(f => ({ ...f, status: e.target.value }))}>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                    </select>
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-medium">Company Address</label>
                    <textarea className="form-control" rows={2} placeholder="Street address, city, etc." value={addForm.company_address || ''} onChange={e => setAddForm(f => ({ ...f, company_address: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-medium">Category</label>
                    <input className="form-control" placeholder="e.g. Medical Equipment, Pharmaceuticals" value={addForm.category || ''} onChange={e => setAddForm(f => ({ ...f, category: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-medium">Company Description</label>
                    <textarea className="form-control" rows={3} placeholder="Brief description of the company and its services..." value={addForm.description || ''} onChange={e => setAddForm(f => ({ ...f, description: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-medium">Partnership Goals</label>
                    <textarea className="form-control" rows={2} placeholder="What the vendor hopes to achieve through the partnership..." value={addForm.partnership_goals || ''} onChange={e => setAddForm(f => ({ ...f, partnership_goals: e.target.value }))} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-medium">Company Logo</label>
                    {addLogoPreview && (
                      <div className="mb-2">
                        <img src={addLogoPreview} alt="Logo preview" style={{ maxHeight: '70px', maxWidth: '180px', objectFit: 'contain', border: '1px solid #dee2e6', borderRadius: '4px', padding: '4px' }} />
                      </div>
                    )}
                    <input type="file" className="form-control" accept="image/*" onChange={e => {
                      const file = e.target.files[0];
                      if (file) {
                        setAddLogo(file);
                        const reader = new FileReader();
                        reader.onload = ev => setAddLogoPreview(ev.target.result);
                        reader.readAsDataURL(file);
                      }
                    }} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-medium">Business License / Document</label>
                    <input type="file" className="form-control" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={e => setAddDoc(e.target.files[0])} />
                    <small className="text-muted">PDF, DOC, or image</small>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
                <button
                  className="btn text-white fw-bold"
                  style={{ backgroundColor: '#6B8E23', border: 'none' }}
                  disabled={actionLoading === 'add'}
                  onClick={handleAddSubmit}
                >
                  {actionLoading === 'add' ? <><span className="spinner-border spinner-border-sm me-2" />Adding...</> : <><FaPlus className="me-2" />Add Vendor</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorManagement;
